import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { SectionCard } from "@/components/common/Primitives";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { fmtDate } from "@/lib/format";
import { Building2, CheckCircle2, AlertTriangle, RefreshCw, Link2, Unlink } from "lucide-react";
import { useState } from "react";

const BADGE = {
  not_configured: { label: "Nicht konfiguriert", cls: "bg-muted text-muted-foreground" },
  disconnected: { label: "Nicht verbunden", cls: "bg-amber-500/10 text-amber-700 dark:text-amber-300" },
  connected: { label: "Verbunden", cls: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300" },
};

const SERVICE_LABELS = {
  planner: { label: "Microsoft Planner (Teamaufgaben)", available: true },
  todo: { label: "Microsoft To Do (persönliche Aufgaben)", available: true },
  outlook: { label: "Outlook-Kalender & Erinnerungen", available: false },
  sharepoint: { label: "SharePoint / OneDrive", available: false },
  teams: { label: "Teams-Benachrichtigungen", available: false },
};

export default function MicrosoftCard() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const canWrite = can(user?.role, "integrations:write");

  const { data, isLoading } = useQuery({
    queryKey: ["ms-status"],
    queryFn: async () => (await api.get("/microsoft/status")).data,
  });

  useEffect(() => {
    const m = params.get("microsoft");
    if (!m) return;
    if (m === "connected") toast.success("Microsoft 365 verbunden");
    else if (m === "invalid_state") toast.error("Verbindung abgelaufen – bitte erneut versuchen");
    else toast.error("Microsoft-365-Verbindung fehlgeschlagen");
    qc.invalidateQueries({ queryKey: ["ms-status"] });
    const next = new URLSearchParams(params);
    next.delete("microsoft");
    setParams(next, { replace: true });
  }, [params, setParams, qc]);

  const status = data?.status || "not_configured";
  const b = BADGE[status] || BADGE.not_configured;
  const conn = data?.connection;

  const connect = async () => {
    try {
      const r = await api.get("/microsoft/connect");
      window.location.assign(r.data.authorization_url);
    } catch (err) { toast.error(apiError(err)); }
  };
  const verify = async () => {
    try { const r = await api.post("/microsoft/verify"); toast.success(`Verbindung ok: ${r.data.user?.name || ""}`); qc.invalidateQueries({ queryKey: ["ms-status"] }); }
    catch (err) { toast.error(apiError(err)); }
  };
  const disconnect = async () => {
    try { await api.delete("/microsoft/disconnect"); toast.success("Verbindung getrennt"); qc.invalidateQueries({ queryKey: ["ms-status"] }); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <SectionCard title={<span className="flex items-center gap-2"><Building2 className="h-4 w-4 text-muted-foreground" />Microsoft 365</span>}>
      <div className="flex flex-col gap-3" data-testid="ms-integration-card">
        <Badge className={`w-fit ${b.cls}`} data-testid="ms-status-badge">{b.label}</Badge>
        <p className="text-sm text-muted-foreground">
          Entra ID, Microsoft Planner & To Do über Microsoft Graph. Aufgaben aus primeNetConnect
          lassen sich mit Microsoft 365 synchronisieren.
        </p>

        {isLoading ? <p className="text-xs text-muted-foreground">Lade…</p> : (
          <>
            {status === "not_configured" && (
              <div className="rounded-md border border-dashed bg-muted/40 p-3 text-xs text-muted-foreground space-y-1">
                <p className="font-medium text-foreground">Vom Plattform-Administrator noch nicht konfiguriert.</p>
                <p>Es sind eine Azure Entra ID App-Registrierung (Client-ID & Secret) sowie folgende Redirect-URI erforderlich:</p>
                <code className="block break-all rounded bg-background px-2 py-1">{data?.redirect_uri || "—"}</code>
              </div>
            )}

            {status === "disconnected" && canWrite && (
              <Button onClick={connect} size="sm" className="w-fit gap-1.5 bg-accent text-accent-foreground hover:bg-accent/90" data-testid="ms-connect-button">
                <Link2 className="h-4 w-4" /> Microsoft 365 verbinden
              </Button>
            )}

            {status === "connected" && (
              <div className="space-y-3">
                <div className="rounded-md border bg-card p-3 text-xs space-y-1">
                  <div className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400"><CheckCircle2 className="h-3.5 w-3.5" /> {conn?.ms_user_name || "Verbunden"}</div>
                  {conn?.ms_tenant_id && <div className="text-muted-foreground">Microsoft-Tenant: <span className="font-mono">{conn.ms_tenant_id}</span></div>}
                  {conn?.connected_at && <div className="text-muted-foreground">Verbunden seit: {fmtDate(conn.connected_at)}</div>}
                  {conn?.last_sync_at && <div className="text-muted-foreground">Letzte Synchronisation: {fmtDate(conn.last_sync_at)}</div>}
                  {conn?.last_error && <div className="flex items-start gap-1 text-destructive"><AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />{conn.last_error}</div>}
                </div>

                {canWrite && <ServiceConfig conn={conn} onSaved={() => qc.invalidateQueries({ queryKey: ["ms-status"] })} />}

                {canWrite && (
                  <div className="flex gap-2">
                    <Button onClick={verify} variant="outline" size="sm" className="gap-1.5" data-testid="ms-verify-button"><RefreshCw className="h-4 w-4" /> Prüfen</Button>
                    <Button onClick={disconnect} variant="outline" size="sm" className="gap-1.5 text-destructive" data-testid="ms-disconnect-button"><Unlink className="h-4 w-4" /> Trennen</Button>
                  </div>
                )}
              </div>
            )}

            <details className="text-xs text-muted-foreground">
              <summary className="cursor-pointer select-none">Erforderliche Microsoft-Graph-Berechtigungen</summary>
              <ul className="mt-2 space-y-1">
                {(data?.required_permissions || []).map((p) => (
                  <li key={p.scope} className="flex items-start gap-2">
                    <code className="shrink-0 rounded bg-muted px-1">{p.scope}</code>
                    <span>{p.purpose}{p.admin_consent ? " · Admin-Zustimmung nötig" : ""}</span>
                  </li>
                ))}
              </ul>
            </details>
          </>
        )}
      </div>
    </SectionCard>
  );
}

function ServiceConfig({ conn, onSaved }) {
  const [services, setServices] = useState(conn?.services || {});
  const [sync, setSync] = useState(conn?.sync || {});
  const [saving, setSaving] = useState(false);

  const toggle = (k) => setServices((s) => ({ ...s, [k]: !s[k] }));
  const setSyncVal = (k, v) => setSync((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try { await api.put("/microsoft/services", { services, sync }); toast.success("Einstellungen gespeichert"); onSaved(); }
    catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-3 rounded-md border bg-muted/30 p-3">
      <p className="text-xs font-medium">Aktivierte Dienste</p>
      <div className="space-y-2">
        {Object.entries(SERVICE_LABELS).map(([k, meta]) => (
          <div key={k} className="flex items-center justify-between gap-2">
            <Label className="text-xs font-normal">{meta.label}{!meta.available && <span className="ml-1 text-[10px] text-muted-foreground">(bald)</span>}</Label>
            <Switch checked={!!services[k]} onCheckedChange={() => toggle(k)} disabled={!meta.available} data-testid={`ms-service-${k}`} />
          </div>
        ))}
      </div>
      <div className="grid gap-2 border-t pt-3">
        <div className="space-y-1"><Label className="text-xs">Planner Plan-ID (für Teamaufgaben)</Label>
          <Input className="h-8 text-xs" value={sync.planner_plan_id || ""} onChange={(e) => setSyncVal("planner_plan_id", e.target.value)} placeholder="z. B. xqOf28…" data-testid="ms-plan-id" /></div>
        <div className="space-y-1"><Label className="text-xs">Planner Bucket-ID (optional)</Label>
          <Input className="h-8 text-xs" value={sync.planner_bucket_id || ""} onChange={(e) => setSyncVal("planner_bucket_id", e.target.value)} /></div>
        <div className="space-y-1"><Label className="text-xs">To-Do Listen-ID (optional, sonst Standardliste)</Label>
          <Input className="h-8 text-xs" value={sync.todo_list_id || ""} onChange={(e) => setSyncVal("todo_list_id", e.target.value)} /></div>
      </div>
      <Button onClick={save} size="sm" disabled={saving} className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="ms-services-save">Speichern</Button>
    </div>
  );
}
